import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, IconButton, Typography, Button, Tooltip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CustomInput from 'components/CustomInput/CustomInput.js';
import Autocomplete from '@mui/material/Autocomplete';
import { default as Autotextfield } from '@mui/material/TextField';
import GridContainer from '../Grid/GridContainer';
import GridItem from '../Grid/GridItem';
import axios from 'axios';
import config from '../../config/config.json';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { store } from '../../stores/store';
import {
  setRarity,
  setType,
  setItemType,
  setNewItems,
  setEthereal,
  setUnidentified,
  setLimitFilter,
  clearFilterValues,
  setSearchTerm,
  setSavedSearches,
  setSavedSearchSent,
  setViewType,
  setPriceFilter,
  addStatFilterGroup,
  removeStatFilterGroup,
  addFilterToGroup,
  removeFilterFromGroup,
  updateFilterInGroup,
  toggleFilterBubble as _toggleFilterBubble,
} from '../../features/filters/filterSlice';
import { RootState } from '../../stores/store';
import { secondaryColor } from '../../assets/jss/material-kit-react';
import d2rsIcon from '../../assets/img/d2rsiconblock.png';
import d2rIcon from '../../assets/img/d2r-logo.png';
import { useStaticData } from '../../hooks/useStaticData';
import { FilterAltOffOutlined } from '@mui/icons-material';
import { ShoppingCartCheckoutOutlined, ShoppingCartOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import {
  SavedSearch,
  Stat,
  ItemType,
  Category,
  StatFilter,
  FilterState,
} from '../../models/filter';
import { useStyles } from './FiltersBubble.styles';
import { D2RSService } from '../../services/d2rsService';
import ActiveFilters from '../ActiveFilters/ActiveFilters';
import ToolsIcon from 'components/ToolsIcon';

// Add global style for Material UI poppers to ensure they're visible above everything else
const GlobalStyles = () => {
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    if (!styleRef.current) {
      const style = document.createElement('style');
      style.innerHTML = `
        .MuiPopper-root, .MuiAutocomplete-popper, .MuiPopover-root {
          z-index: 99999 !important;
        }
      `;
      document.head.appendChild(style);
      styleRef.current = style;
    }

    return () => {
      if (styleRef.current && document.head.contains(styleRef.current)) {
        document.head.removeChild(styleRef.current);
      }
    };
  }, []);

  return null;
};

const getColorFromRarity = (rarity: string): string => {
  const rarityColors: { [key: string]: string } = {
    WHITE: '#FFFFFF',
    SET: '#00FF00',
    MAGIC: '#4169E1',
    UNIQUE: '#A59263',
    CRAFTED: '#FFA800',
    RARE: '#FFFF00',
    NORMAL: '#FFFFFF',
    RUNEWORD: '#A59263',
    RUNE: '#A59263',
  };

  return rarityColors[rarity?.toUpperCase()] || '#FFFFFF';
};

export default function FiltersBubbleContent() {
  const classes = useStyles();
  const { t } = useTranslation();
  const showFilterBubble = parseInt(localStorage.getItem('show_filter_bubble') || '1', 10) === 1;
  const sessionToken = localStorage.getItem('session_token');
  const dispatch = useDispatch();
  const [_savedSearchFilter, _setSavedSearchFilter] = useState('');
  const [_isLoadingSearches, _setIsLoadingSearches] = useState(false);
  const [currentStatMin, setCurrentStatMin] = useState(1);
  const [currentStatMax, setCurrentStatMax] = useState(99999);
  const navigate = useNavigate();

  useStaticData(); // Just call the hook to trigger data fetching

  // Get filter states from Redux
  const {
    newItems,
    ethereal,
    unidentified,
    limitFilter,
    savedSearches,
    rarity,
    type,
    itemType,
    viewType,
    priceFilter,
    statFilterGroups,
  } = useSelector((state: RootState) => state.filters);

  // Get static data from Redux
  const { types, categories, rarities, stats, isLoading } = useSelector(
    (state: RootState) => state.staticData
  );

  const getSavedSearches = useCallback(async () => {
    const url = config.backend_base_url + 'ml/search/mine';
    _setIsLoadingSearches(true);
    try {
      const response = await axios.get(url, { headers: { 'session-token': sessionToken } });
      dispatch(setSavedSearches(response.data || []));
    } catch (error) {
      console.error('Error fetching saved searches:', error);
      toast.error('Error occurred while fetching saved searches');
    } finally {
      _setIsLoadingSearches(false);
    }
  }, [sessionToken, dispatch]);

  useEffect(() => {
    if (sessionToken) {
      getSavedSearches();
    }
  }, [sessionToken, getSavedSearches]);

  const _handleSavedSearchClick = (search: SavedSearch) => {
    if (search.search_term) {
      dispatch(setSearchTerm(search.search_term));
      dispatch(setSavedSearchSent(true));
    }
  };

  // Filter saved searches based on search input
  const _filteredSavedSearches = savedSearches.filter((search) =>
    search.search_name.toLowerCase().includes(_savedSearchFilter.toLowerCase())
  );

  const countActiveFilters = useCallback(() => {
    let count = 0;
    if (rarity !== '') count++;
    if (type !== '') count++;
    if (itemType !== '') count++;
    // Count all stat filters across all groups
    statFilterGroups.forEach((group) => {
      if (group.filters.length > 0) count++;
    });
    if (unidentified !== '') count++;
    if (ethereal !== '') count++;
    if (newItems !== '') count++;
    if (limitFilter !== 100) count++;
    if (viewType !== 'all') count++;
    if (priceFilter !== 'all') count++;
    return count;
  }, [
    rarity,
    type,
    itemType,
    statFilterGroups,
    unidentified,
    ethereal,
    newItems,
    limitFilter,
    viewType,
    priceFilter,
  ]);

  // Helper to generate unique IDs for filter groups
  const generateUniqueId = () => {
    return `group_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  };

  // Modify to add stat filter to a specific group
  const handleAddStatFilter = (stat: Stat, groupId?: string) => {
    // Create a proper StatFilter object
    const newFilter: StatFilter = {
      id: stat.id,
      name: stat.name,
      min: currentStatMin,
      max: currentStatMax,
    };

    // If no groups exist, create one
    if (statFilterGroups.length === 0) {
      const newGroupId = generateUniqueId();
      dispatch(
        addStatFilterGroup({
          id: newGroupId,
          filters: [newFilter],
        })
      );
    } else if (groupId) {
      // Add to the specified group
      dispatch(addFilterToGroup({ groupId, filter: newFilter }));
    } else {
      // Add to the first group by default
      const firstGroup = statFilterGroups[0];
      dispatch(addFilterToGroup({ groupId: firstGroup.id, filter: newFilter }));
    }

    setCurrentStatMin(1);
    setCurrentStatMax(99999);
  };

  // Add method to create a new filter group
  const handleAddFilterGroup = () => {
    dispatch(
      addStatFilterGroup({
        id: generateUniqueId(),
        filters: [],
      })
    );
  };

  // Modify to remove a stat filter from a specific group
  const handleRemoveStatFilter = (groupId: string, statId: string) => {
    dispatch(removeFilterFromGroup({ groupId, filterId: statId }));
  };

  // Modify to update a stat filter in a specific group
  const handleUpdateStatFilter = (groupId: string, statId: string, min: number, max: number) => {
    // Find the filter to preserve all its properties
    const group = statFilterGroups.find((g) => g.id === groupId);
    if (group) {
      const filter = group.filters.find((f) => f.id === statId);
      if (filter) {
        const updatedFilter: StatFilter = {
          ...filter,
          min,
          max,
        };
        dispatch(updateFilterInGroup({ groupId, filter: updatedFilter }));
      }
    }
  };

  // Method to remove an entire filter group
  const handleRemoveFilterGroup = (groupId: string) => {
    dispatch(removeStatFilterGroup(groupId));
  };

  // Modify the stat filter section render - moved inside the component
  const renderStatFilterSection = () => {
    return (
      <div>
        <GridContainer>
          <GridItem xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">
                {t('filters.statFilter')}
              </Typography>
              <GridContainer>
                <GridItem xs={12}>
                  {isLoading.stats ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Autocomplete<Stat>
                      id="searchForStatAutocomplete"
                      options={stats}
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <Autotextfield
                          {...params}
                          label={t('filters.statFilter')}
                          sx={{
                            '& + .MuiAutocomplete-popper': {
                              zIndex: 99999,
                            },
                          }}
                        />
                      )}
                      onChange={(_, value) => {
                        if (value) {
                          handleAddStatFilter(value);
                        }
                      }}
                      value={null}
                    />
                  )}
                </GridItem>
                <GridItem xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const levelStat = stats.find((s) =>
                          s.name.toLowerCase().includes('level req')
                        );
                        if (levelStat) {
                          // Create a proper StatFilter object instead of passing min/max in a Stat
                          const levelFilter: StatFilter = {
                            id: levelStat.id,
                            name: levelStat.name,
                            min: 9,
                            max: 9,
                          };
                          // If no groups exist, create one
                          if (statFilterGroups.length === 0) {
                            const newGroupId = generateUniqueId();
                            dispatch(
                              addStatFilterGroup({
                                id: newGroupId,
                                filters: [levelFilter],
                              })
                            );
                          } else {
                            // Add to the first group
                            const firstGroup = statFilterGroups[0];
                            dispatch(
                              addFilterToGroup({
                                groupId: firstGroup.id,
                                filter: levelFilter,
                              })
                            );
                          }
                        }
                      }}
                      sx={{
                        textTransform: 'none',
                        border: `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      LLD 9
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const levelStat = stats.find((s) =>
                          s.name.toLowerCase().includes('level req')
                        );
                        if (levelStat) {
                          // Create a proper StatFilter object instead of passing min/max in a Stat
                          const levelFilter: StatFilter = {
                            id: levelStat.id,
                            name: levelStat.name,
                            min: 30,
                            max: 30,
                          };
                          // If no groups exist, create one
                          if (statFilterGroups.length === 0) {
                            const newGroupId = generateUniqueId();
                            dispatch(
                              addStatFilterGroup({
                                id: newGroupId,
                                filters: [levelFilter],
                              })
                            );
                          } else {
                            // Add to the first group
                            const firstGroup = statFilterGroups[0];
                            dispatch(
                              addFilterToGroup({
                                groupId: firstGroup.id,
                                filter: levelFilter,
                              })
                            );
                          }
                        }
                      }}
                      sx={{
                        textTransform: 'none',
                        border: `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      LLD 30
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const levelStat = stats.find((s) =>
                          s.name.toLowerCase().includes('level req')
                        );
                        if (levelStat) {
                          // Create a proper StatFilter object instead of passing min/max in a Stat
                          const levelFilter: StatFilter = {
                            id: levelStat.id,
                            name: levelStat.name,
                            min: 49,
                            max: 49,
                          };
                          // If no groups exist, create one
                          if (statFilterGroups.length === 0) {
                            const newGroupId = generateUniqueId();
                            dispatch(
                              addStatFilterGroup({
                                id: newGroupId,
                                filters: [levelFilter],
                              })
                            );
                          } else {
                            // Add to the first group
                            const firstGroup = statFilterGroups[0];
                            dispatch(
                              addFilterToGroup({
                                groupId: firstGroup.id,
                                filter: levelFilter,
                              })
                            );
                          }
                        }
                      }}
                      sx={{
                        textTransform: 'none',
                        border: `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      LLD 49
                    </Button>
                  </Box>
                </GridItem>

                {/* New button to add a new filter group */}
                {statFilterGroups.length > 0 && (
                  <GridItem xs={12}>
                    <Button
                      variant="outlined"
                      onClick={handleAddFilterGroup}
                      startIcon={<AddIcon />}
                      sx={{
                        textTransform: 'none',
                        border: `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                        mb: 2,
                      }}
                    >
                      {t('filters.addStatFilterGroup', 'Add New Filter Group (AND)')}
                    </Button>
                  </GridItem>
                )}

                {/* Render each filter group */}
                {statFilterGroups.map((group, groupIndex) => (
                  <GridItem xs={12} key={group.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        mb: 2,
                        border: `1px solid ${secondaryColor}33`,
                        borderRadius: '8px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="subtitle2">
                          {t('filters.statFilterGroup', 'Stat Filter Group')} {groupIndex + 1}
                          {groupIndex > 0 && ' (AND)'}
                        </Typography>
                        {statFilterGroups.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFilterGroup(group.id)}
                            sx={{ color: 'error.main' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Render filters in this group */}
                      {group.filters.map((filter, filterIndex) => (
                        <Box
                          key={filter.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {filterIndex > 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                alignSelf: 'center',
                                fontWeight: 'bold',
                                color: 'primary.main',
                                my: 1,
                              }}
                            >
                              OR
                            </Typography>
                          )}

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 2,
                              border: `1px solid ${secondaryColor}33`,
                              borderRadius: '8px',
                            }}
                          >
                            <Typography sx={{ minWidth: '120px' }}>{filter.name}</Typography>
                            <CustomInput
                              labelText={t('filters.minValue')}
                              id={`min-${group.id}-${filter.id}`}
                              value={filter.min.toString()}
                              onChange={(params) =>
                                handleUpdateStatFilter(
                                  group.id,
                                  filter.id,
                                  parseInt(params.target.value),
                                  filter.max
                                )
                              }
                              inputProps={{
                                type: 'number',
                                min: 1,
                                max: 99999,
                              }}
                              formControlProps={{
                                fullWidth: true,
                              }}
                            />
                            <CustomInput
                              labelText={t('filters.maxValue')}
                              id={`max-${group.id}-${filter.id}`}
                              value={filter.max.toString()}
                              onChange={(params) =>
                                handleUpdateStatFilter(
                                  group.id,
                                  filter.id,
                                  filter.min,
                                  parseInt(params.target.value)
                                )
                              }
                              inputProps={{
                                type: 'number',
                                min: 0,
                                max: 99999,
                              }}
                              formControlProps={{
                                fullWidth: true,
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveStatFilter(group.id, filter.id)}
                              sx={{
                                color: (theme) =>
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.7)'
                                    : 'rgba(0, 0, 0, 0.7)',
                                '&:hover': {
                                  color: (theme) =>
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.9)'
                                      : 'rgba(0, 0, 0, 0.9)',
                                },
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}

                      {/* Add OR condition button */}
                      {group.filters.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                          <Autocomplete<Stat>
                            id={`add-or-filter-${group.id}`}
                            options={stats}
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                              <Autotextfield
                                {...params}
                                label={t('filters.addOrCondition', 'Add OR condition')}
                                size="small"
                                sx={{ minWidth: '250px' }}
                              />
                            )}
                            onChange={(_, value) => {
                              if (value) {
                                handleAddStatFilter(value, group.id);
                              }
                            }}
                            value={null}
                            componentsProps={{
                              popper: {
                                sx: {
                                  zIndex: 99999,
                                },
                              },
                            }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, ml: 1, alignItems: 'center' }}>
                            <CustomInput
                              labelText={t('filters.minValue')}
                              id={`or-min-${group.id}`}
                              value={currentStatMin.toString()}
                              onChange={(e) => setCurrentStatMin(parseInt(e.target.value))}
                              inputProps={{
                                type: 'number',
                                min: 1,
                                max: 99999,
                                style: { width: '60px' },
                              }}
                              formControlProps={{
                                margin: 'dense',
                              }}
                            />
                            <CustomInput
                              labelText={t('filters.maxValue')}
                              id={`or-max-${group.id}`}
                              value={currentStatMax.toString()}
                              onChange={(e) => setCurrentStatMax(parseInt(e.target.value))}
                              inputProps={{
                                type: 'number',
                                min: 1,
                                max: 99999,
                                style: { width: '60px' },
                              }}
                              formControlProps={{
                                margin: 'dense',
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </GridItem>
                ))}
              </GridContainer>
            </Box>
          </GridItem>
        </GridContainer>
      </div>
    );
  };

  if (!showFilterBubble) {
    return null;
  }

  return (
    <Box>
      <GlobalStyles />
      <Box>
        <Box className={classes.toolbarContainer}>
          <ToolsIcon
            tooltipText={t('filters.toolsTooltip', 'These tools can be used on this filter')}
          />
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip
              title={t('filters.searchTooltip', 'Apply filters and search')}
              placement="top"
              arrow
            >
              <IconButton
                color="primary"
                onClick={() => {
                  const currentFilters = (store.getState() as any).filters;
                  D2RSService.navigateWithFilters(
                    currentFilters,
                    navigate,
                    '/shop', // Navigate to the items search page
                    dispatch
                  );
                  dispatch(_toggleFilterBubble());
                }}
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.dark,
                  },
                  width: 40,
                  height: 40,
                }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
            {countActiveFilters() > 0 && (
              <Tooltip title={t('filters.clearTooltip', 'Clear all filters')} placement="top" arrow>
                <IconButton
                  color="error"
                  onClick={() => dispatch(clearFilterValues())}
                  sx={{
                    color: (theme) => theme.palette.error.contrastText,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.error.dark,
                    },
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FilterAltOffOutlined sx={{ width: 20, height: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            mb: 2,
            border: `1px solid ${secondaryColor}33`,
            borderRadius: '8px',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
              {t('filters.selectedFilters')}
            </Typography>
            <ActiveFilters hideStatFilters={false} />
          </Box>
        </Box>

        <div className={classes.filterSection}>
          <div>
            <div className={classes.columnHeader}>
              <img src={d2rIcon} alt="D2R" className={classes.headerImage} />
              {t('filters.d2rFilters')}
            </div>
          </div>

          <div>
            <GridContainer>
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('filters.rarityFilter')}
                  </Typography>
                  {isLoading.rarities ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => dispatch(setRarity(rarity === '' ? '' : ''))}
                        sx={{
                          textTransform: 'none',
                          border: `2px solid ${secondaryColor}33`,
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.7)'
                              : 'rgba(0, 0, 0, 0.7)',
                          '&:hover': {
                            border: `2px solid ${secondaryColor}`,
                          },
                        }}
                      >
                        {t('filters.all')}
                      </Button>
                      {(rarities && Array.isArray(rarities) ? rarities : []).map((rarityItem) => (
                        <Button
                          key={rarityItem.id}
                          variant="outlined"
                          onClick={() =>
                            dispatch(
                              setRarity(
                                rarity === rarityItem.name.toLowerCase()
                                  ? ''
                                  : (rarityItem.name.toLowerCase() as FilterState['rarity'])
                              )
                            )
                          }
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            textTransform: 'none',
                            border:
                              rarity === rarityItem.name.toLowerCase()
                                ? `2px solid ${secondaryColor}`
                                : `2px solid ${secondaryColor}33`,
                            color: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.7)'
                                : 'rgba(0, 0, 0, 0.7)',
                            '&:hover': {
                              border: `2px solid ${secondaryColor}`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: getColorFromRarity(rarityItem.name),
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }}
                          />
                          {rarityItem.name}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
              </GridItem>
            </GridContainer>
          </div>

          <div>
            <GridContainer>
              <GridItem xs={12}>
                {isLoading.categories ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Autocomplete<Category>
                    id="categoryFilter"
                    options={categories}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              position: 'relative',
                              width: '72px',
                              height: '24px',
                            }}
                          >
                            {option.types?.slice(0, 3).map((type, index) => (
                              <img
                                key={type.id}
                                src={`${config.backend_base_url}skin/${type.skin_path}${ethereal === 'eth' ? '_eth' : ''}`}
                                alt={type.name}
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  objectFit: 'contain',
                                  position: 'absolute',
                                  left: `${index * 16}px`,
                                  zIndex: 3 - index,
                                }}
                              />
                            ))}
                          </Box>
                          {option.name}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <Autotextfield {...params} label={t('filters.categoryFilter')} />
                    )}
                    onChange={(_, value) => {
                      dispatch(setType(value?.name || ''));
                      if (value?.name) {
                        dispatch(setItemType(''));
                      }
                    }}
                    value={
                      categories && Array.isArray(categories) && typeof type === 'string'
                        ? categories.find(
                            (category) => category.name.toLowerCase() === type.toLowerCase()
                          ) || null
                        : null
                    }
                    componentsProps={{
                      popper: {
                        sx: {
                          zIndex: 99999,
                        },
                      },
                    }}
                  />
                )}
              </GridItem>
              <GridItem xs={12}>
                {isLoading.types ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Autocomplete<ItemType>
                    id="typeFilter"
                    options={types}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {option.skin_path && (
                            <img
                              src={`${config.backend_base_url}skin/${option.skin_path}${ethereal === 'eth' ? '_eth' : ''}`}
                              alt={option.name}
                              style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                            />
                          )}
                          {option.name}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <Autotextfield {...params} label={t('filters.itemTypeFilter')} />
                    )}
                    onChange={(_, value) => {
                      dispatch(setItemType(value?.name || ''));
                      if (value?.name) {
                        dispatch(setType(''));
                      }
                    }}
                    value={
                      types && Array.isArray(types) && typeof itemType === 'string'
                        ? types.find(
                            (typeItem) => typeItem.name.toLowerCase() === itemType.toLowerCase()
                          ) || null
                        : null
                    }
                    componentsProps={{
                      popper: {
                        sx: {
                          zIndex: 99999,
                        },
                      },
                    }}
                  />
                )}
              </GridItem>
            </GridContainer>
          </div>

          <div>
            <GridContainer>
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('filters.unidFilter')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setUnidentified(unidentified === '' ? 'unid' : ''))}
                      sx={{
                        textTransform: 'none',
                        border:
                          unidentified === 'unid'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('filters.unidItemsOnly')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setUnidentified(unidentified === '' ? 'unid' : ''))}
                      sx={{
                        textTransform: 'none',
                        border:
                          unidentified === 'unid'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('filters.excludeUnidItems')}
                    </Button>
                  </Box>
                </Box>
              </GridItem>
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('filters.ethFilter')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setEthereal(ethereal === '' ? 'eth' : ''))}
                      sx={{
                        textTransform: 'none',
                        border:
                          ethereal === 'eth'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('filters.ethItemsOnly')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setEthereal(ethereal === '' ? 'non-eth' : ''))}
                      sx={{
                        textTransform: 'none',
                        border:
                          ethereal === 'non-eth'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('filters.excludeEthItems')}
                    </Button>
                  </Box>
                </Box>
              </GridItem>
            </GridContainer>
          </div>

          <div>{renderStatFilterSection()}</div>
        </div>

        <div className={classes.filterSectionD2RS}>
          <div>
            <div className={classes.columnHeader}>
              <img src={d2rsIcon} alt="D2RS" className={classes.headerImage} />
              {t('filters.d2rsFilters')}
            </div>
            <GridContainer>
              <GridItem xs={12}>
                <CustomInput
                  labelText={t('filters.searchLimit')}
                  id="limitFilter"
                  value={limitFilter.toString()}
                  onChange={(params) => dispatch(setLimitFilter(parseInt(params.target.value)))}
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    type: 'number',
                    autoComplete: 'off',
                  }}
                />
              </GridItem>
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('filters.newItemsFilter')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setNewItems(newItems === '' ? 'new-items-only' : ''))}
                      sx={{
                        textTransform: 'none',
                        border:
                          newItems === 'new-items-only'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('filters.newItemsOnly')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setNewItems(newItems === '' ? 'no-new-items' : ''))}
                      sx={{
                        textTransform: 'none',
                        border:
                          newItems === 'no-new-items'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('filters.excludeNewItems')}
                    </Button>
                  </Box>
                </Box>
              </GridItem>
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('search.viewType.title', 'View Type')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setViewType('all'))}
                      sx={{
                        textTransform: 'none',
                        border:
                          viewType === 'all'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ShoppingCartCheckoutOutlined sx={{ fontSize: 18 }} />
                      <ShoppingCartOutlined sx={{ fontSize: 18 }} />
                      {t('search.viewType.all', 'All')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setViewType('instant'))}
                      sx={{
                        textTransform: 'none',
                        border:
                          viewType === 'instant'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ShoppingCartCheckoutOutlined sx={{ fontSize: 18 }} />
                      {t('search.viewType.instantItems', 'Instant Items')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setViewType('products'))}
                      sx={{
                        textTransform: 'none',
                        border:
                          viewType === 'products'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ShoppingCartOutlined sx={{ fontSize: 18 }} />
                      {t('search.viewType.products', 'Products')}
                    </Button>
                  </Box>
                </Box>
              </GridItem>
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('search.priceFilter.title', 'Price Filter')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setPriceFilter('all'))}
                      sx={{
                        textTransform: 'none',
                        border:
                          priceFilter === 'all'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('search.priceFilter.all', 'All')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setPriceFilter('priced'))}
                      sx={{
                        textTransform: 'none',
                        border:
                          priceFilter === 'priced'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('search.priceFilter.priced', 'Priced')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => dispatch(setPriceFilter('unpriced'))}
                      sx={{
                        textTransform: 'none',
                        border:
                          priceFilter === 'unpriced'
                            ? `2px solid ${secondaryColor}`
                            : `1px solid ${secondaryColor}33`,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          border: `2px solid ${secondaryColor}`,
                        },
                      }}
                    >
                      {t('search.priceFilter.unpriced', 'Unpriced')}
                    </Button>
                  </Box>
                </Box>
              </GridItem>
            </GridContainer>
          </div>
        </div>

        <div className={classes.filterSectionD2RS}>
          <div>
            <div className={classes.columnHeader}>
              <img src={d2rsIcon} alt="D2RS" className={classes.headerImage} />
              {t('filters.savedSearches')}
            </div>
            <GridContainer>
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <CustomInput
                    labelText={t('filters.searchSavedSearches')}
                    id="savedSearchFilter"
                    value={_savedSearchFilter}
                    onChange={(e) => _setSavedSearchFilter(e.target.value)}
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      autoComplete: 'off',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      mb: 2,
                    }}
                  >
                    {_isLoadingSearches ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : _filteredSavedSearches.length > 0 ? (
                      _filteredSavedSearches.map((search) => (
                        <Button
                          key={search.search_name}
                          variant="outlined"
                          size="small"
                          onClick={() => _handleSavedSearchClick(search)}
                          fullWidth
                          sx={{
                            textTransform: 'none',
                            border: `1px solid ${secondaryColor}33`,
                            color: (theme) =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.7)'
                                : 'rgba(0, 0, 0, 0.7)',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: 'inherit' }}>
                            {search.search_count}x
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: 'inherit', flex: 1, textAlign: 'center' }}
                          >
                            {search.search_name}
                          </Typography>
                        </Button>
                      ))
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        href="/saved-searches"
                        target="_blank"
                        fullWidth
                        sx={{
                          textTransform: 'none',
                          border: `1px solid ${secondaryColor}33`,
                          color: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.7)'
                              : 'rgba(0, 0, 0, 0.7)',
                          '&:hover': {
                            border: `2px solid ${secondaryColor}`,
                          },
                        }}
                      >
                        {t('filters.createSavedSearches')}
                      </Button>
                    )}
                  </Box>
                </Box>
              </GridItem>
            </GridContainer>
          </div>
        </div>
      </Box>
    </Box>
  );
}
